import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { getMenuItemById, infiniteScrollItemsFetchLimit, hasFeature, genericErrorToast } from '@app/helpers';
import {
    IProvisioningPolicy,
    ITableHead,
    IListPageBarEnabled,
    FilterEnum,
    IActiveFilter,
    IFabMenu,
    IProvisioningPolicyForDelete,
    IDotMenu,
    OrderType,
    Order
} from '@app/models';
import {
    handleGetProvisioningPoliciesData,
    setOrderByDirection,
    setOrderByProperty,
    resetState,
    getModalSwitch,
    resetScroll
} from '@app/store/actions';
import { FabMenu, IModalData, ModalComponent, PasslyTable } from '@app/components';
import { hasPermission } from '@app/helpers/permissions';
import { ManageSystem, PolicyCreation } from '@app/store/constants';
import { FormMode } from '@app/helpers/enums';
import { confirm } from '@app/components/Modal/confirmModal';
import { provisioningPoliciesService } from '@app/services';
import { AddEditProvisioningPolicyDialog } from './dialogs';

interface IProvisioningPolicyTableData {
    checked: boolean;
    checkboxDisabled: boolean;
    displayName: string;
    lastModified: Date;
    id: string;
    isDefault: boolean;
}

enum TableHeadEnum {
    displayName = 'displayName',
    lastModified = 'lastModified'
}

const ProvisioningPoliciesPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Provisioning);
    const provPolicyInfiniteScroll = infiniteScrollItemsFetchLimit;

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('DisplayName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IProvisioningPolicyTableData[]>([]);

    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const policies: IProvisioningPolicy[] = useSelector((state: any) => state.provisioningPolicies.provisioningPolicies);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: true
    };

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const editRow = (e: any) => {
        dispatch(getModalSwitch(true));

        setModalData({
            headerTitle: translation('EDIT'),
            modalSwitcher: modalSwitchState,
            form: (
                <AddEditProvisioningPolicyDialog
                    formMode={FormMode.Edit}
                    policyId={e.currentTarget.dataset.id}
                    handleRefresh={handlePageRefreshFromDialog}
                />
            )
        });
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'POLICY_NAME',
            type: 'link',
            onClick: editRow,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.displayName,
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'POLICIES_LAST_MODIFIED',
            type: 'dateTime'
        }
    ];

    const menu: IFabMenu = {
        name: 'NEW_PROV_POLICY',
        actions: [
            {
                name: 'NEW_PROV_POLICY',
                method: () => addNewPolicy(),
                iconElement: <i className='icon icon-provisioning' />
            }
        ]
    };

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        if (!loadingData && policies) {
            let newRows = policies.map((policy: IProvisioningPolicy) => {
                const data: IProvisioningPolicyTableData = {
                    checked: false,
                    checkboxDisabled: policy.IsDefault,
                    displayName: policy.DisplayName,
                    lastModified: policy.LastModified,
                    id: policy.Id,
                    isDefault: policy.IsDefault
                };
                return data;
            });

            if (policies.length === 0) {
                setRows([]);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, policies]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetProvisioningPoliciesData(orderBy, order, provPolicyInfiniteScroll, skipCount, isReset, activeFilters));
    };

    const addNewPolicy = () => {
        dispatch(getModalSwitch(true));

        setModalData({
            headerTitle: translation('NEW_PROV_POLICY'),
            modalSwitcher: modalSwitchState,
            form: <AddEditProvisioningPolicyDialog formMode={FormMode.Add} handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const deletePolicy = (ids: string[]) => {
        if (ids.length > 0) {
            let jsonObject: IProvisioningPolicyForDelete = {} as IProvisioningPolicyForDelete;
            jsonObject.Policies = [];

            ids.forEach(function (id: string) {
                jsonObject.Policies.push(id);
            });

            provisioningPoliciesService.deletePolicy(jsonObject, genericErrorToast, handleDeleteSuccess);
        }
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const deletePolicyAreYouSure = (ids: any) => {
        confirm(translation('DELETE_POLICY_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_POLICY_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deletePolicy(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deletePolicyAreYouSure(ids);
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        confirm(
            translation('DELETE_MANY_POLICIES_BODY'),
            translation('DELETE'),
            translation('CANCEL'),
            translation('DELETE_MANY_POLICIES_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                deletePolicy(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    }, [rows]);

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    return (
        <>
            <ModalComponent {...modalData} />

            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={orderBy}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={rows.length}
                onFetch={fetchMoreData}
                withCheckboxes={hasPermission([ManageSystem])}
                actionMenuItems={actionMenuItems}
                emptyListHelper={translation('EMPTY_POLICY_LIST')}
                loadingData={loadingData}
                editRow={hasPermission([ManageSystem]) ? editRow : undefined}
                deleteRow={hasPermission([ManageSystem]) ? deleteRow : undefined}
            />

            {!loadingData && hasFeature([PolicyCreation]) && hasPermission([ManageSystem]) && <FabMenu menu={menu} hideTooltip={true} />}
        </>
    );
};

export default ProvisioningPoliciesPage;
